import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { 
  Users, 
  Crown, 
  Image, 
  TrendingUp, 
  Calendar,
  Search,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/check`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.is_admin) {
        setIsAdmin(true);
        fetchStats();
        fetchUsers();
      } else {
        toast.error('Admin access required');
        navigate('/library');
      }
    } catch (error) {
      toast.error('Access denied');
      navigate('/library');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTierBadge = (tier) => {
    switch (tier) {
      case 'pro':
        return <span className="px-2 py-1 bg-[#d4af37] text-[#0a2540] text-xs font-semibold rounded-full">Pro</span>;
      case 'standard':
        return <span className="px-2 py-1 bg-[#0a2540] text-white text-xs font-semibold rounded-full">Standard</span>;
      default:
        return <span className="px-2 py-1 bg-slate-200 text-slate-600 text-xs font-semibold rounded-full">Free</span>;
    }
  };

  const filteredUsers = users.filter(user => 
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    
    if (sortField === 'created_at') {
      aVal = new Date(aVal || 0).getTime();
      bVal = new Date(bVal || 0).getTime();
    }
    
    if (sortDir === 'asc') {
      return aVal > bVal ? 1 : -1;
    }
    return aVal < bVal ? 1 : -1;
  });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
        <div className="animate-pulse text-[#0a2540]">Checking access...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <Navbar />
      
      <div className="pt-20 px-4 pb-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-[#0a2540]">
              Admin Dashboard
            </h1>
            <p className="text-slate-600 mt-1">
              User management and stats
            </p>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-xl p-4 border border-slate-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-[#0a2540]/10 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-[#0a2540]" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-[#0a2540]">{stats.total_users}</p>
                <p className="text-sm text-slate-500">Total Users</p>
              </div>

              <div className="bg-white rounded-xl p-4 border border-slate-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-[#d4af37]/10 rounded-lg flex items-center justify-center">
                    <Crown className="w-5 h-5 text-[#d4af37]" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-[#0a2540]">{stats.paid_users}</p>
                <p className="text-sm text-slate-500">Paid Users</p>
              </div>

              <div className="bg-white rounded-xl p-4 border border-slate-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-[#0a2540]">{stats.recent_signups_7d}</p>
                <p className="text-sm text-slate-500">Signups (7 days)</p>
              </div>

              <div className="bg-white rounded-xl p-4 border border-slate-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Image className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-[#0a2540]">{stats.total_assets}</p>
                <p className="text-sm text-slate-500">Total Assets</p>
              </div>
            </div>
          )}

          {/* Tier Breakdown */}
          {stats && (
            <div className="bg-white rounded-xl p-4 border border-slate-100 mb-8">
              <h2 className="font-semibold text-[#0a2540] mb-4">User Tiers</h2>
              <div className="flex gap-6">
                <div>
                  <p className="text-sm text-slate-500">Free</p>
                  <p className="text-xl font-bold text-slate-600">{stats.free_users}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Standard</p>
                  <p className="text-xl font-bold text-[#0a2540]">{stats.standard_users}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Pro</p>
                  <p className="text-xl font-bold text-[#d4af37]">{stats.pro_users}</p>
                </div>
              </div>
            </div>
          )}

          {/* Users Table */}
          <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <h2 className="font-semibold text-[#0a2540]">All Users ({filteredUsers.length})</h2>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-[#d4af37]/20 focus:border-[#d4af37]"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Tier
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Assets
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-[#0a2540]"
                      onClick={() => toggleSort('created_at')}
                    >
                      <div className="flex items-center gap-1">
                        Signed Up
                        {sortField === 'created_at' && (
                          sortDir === 'desc' ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Referrer
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sortedUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-[#0a2540]">{user.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        {getTierBadge(user.subscription_tier)}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-slate-600">{user.asset_count || 0}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-slate-600">{formatDate(user.created_at)}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-slate-500">
                          {user.referred_by_user_id ? '✓ Referred' : '-'}
                        </p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {sortedUsers.length === 0 && (
              <div className="p-8 text-center text-slate-500">
                No users found
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
